import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CockpitZoneView, IncentiveZoneRecordView, MapConfigView } from '@vello/api-client';
import { countdownOf, formatMarketInstant, groupIncentiveZones, marketTimeZone, tileOverride, outlineOf, zoneInForce, zoneStateColor, zoneStateTone } from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { MapCanvas, type MapArea } from '../../components/map-canvas';
import { AUTO_REFRESH_MS, Badge, EmptyState, Notice, Panel, Row, Stack, Table, Text, useAutoRefresh } from '@vello/web-theme';
import { wireMoney } from './shared';
import { useNow } from './use-now';

/**
 * The zone map of the cockpit (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 sections 3.2 and 3.4): the market's approved incentive zones drawn on the market's own map tiles, each coloured by
 * the state of the controller's NEWEST decision for it (short of drivers, short of demand, balanced, quiet, no reliable reading), and beside it the table of every zone - the state, what the
 * controller did and why, and the offers in force there with their source and expiry. The whole-market zone has no outline: it has a row. Without a tile source the map says so and the table
 * still answers. A refused read is an ERROR, never an empty map.
 */
export function ZonesPanel({ market, zoneRecords, zonesError }: { readonly market: string; readonly zoneRecords: readonly IncentiveZoneRecordView[]; readonly zonesError?: string | undefined }): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const [zones, setZones] = useState<readonly CockpitZoneView[] | undefined>(undefined);
  const [config, setConfig] = useState<MapConfigView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const nowMs = useNow();
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '') {
      setZones(undefined);
      return;
    }
    const res = await runtime.incentiveCockpit.zones(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setZones(res.value.zones);
      setError(undefined);
    } else {
      setZones(undefined);
      setError(t('cpt.zones.error', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail]);

  useEffect(() => {
    setZones(undefined);
    setError(undefined);
    void load();
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useEffect(() => {
    setConfig(undefined);
    if (market === '') return undefined;
    let cancelled = false;
    void runtime.ops.mapConfig(market).then((res) => {
      if (!cancelled) setConfig(res.ok ? res.value : undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, market]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(market, markets);
  const grouped = useMemo(() => groupIncentiveZones(zoneRecords), [zoneRecords]);
  const byId = useMemo(() => new Map((zones ?? []).map((z) => [z.zoneId, z])), [zones]);
  const nowIso = new Date(nowMs).toISOString();

  const areas = useMemo<readonly MapArea[]>(
    () =>
      grouped
        .filter((g) => g.approved !== undefined && zoneInForce(g, nowIso))
        .map((g) => {
          const def = (g.approved as IncentiveZoneRecordView).definition;
          return { id: g.zoneId, points: outlineOf(def.geometry as Parameters<typeof outlineOf>[0]), colorVar: zoneStateColor(byId.get(g.zoneId)?.latest?.state), label: def.name };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grouped, byId],
  );

  // Every zone with a name: the drawn ones that are in force, then whatever else the controller evaluated (the whole-market zone, a zone since removed).
  const rows = useMemo(() => {
    const named = new Map(grouped.filter((g) => g.approved !== undefined).map((g) => [g.zoneId, (g.approved as IncentiveZoneRecordView).definition.name]));
    const ids = new Set<string>(['MARKET', ...named.keys(), ...byId.keys()]);
    return [...ids].map((id) => ({ zoneId: id, name: named.get(id), data: byId.get(id) })).sort((a, b) => (a.zoneId === 'MARKET' ? -1 : b.zoneId === 'MARKET' ? 1 : (a.name ?? '').localeCompare(b.name ?? '')));
  }, [grouped, byId]);

  // A refused read of the zones (the drawn outlines, or what the controller decided) is an ERROR, never an empty map.
  if (error !== undefined) return <Notice tone="danger">{error}</Notice>;
  if (zonesError !== undefined) return <Notice tone="danger">{t('cpt.zones.zonesError', { code: zonesError })}</Notice>;

  const label = (r: { zoneId: string; name: string | undefined }): string => (r.zoneId === 'MARKET' ? t('ctl.zone.market') : r.name !== undefined ? t('ctl.zone.drawn', { name: r.name }) : t('ctl.zone.unknown'));

  return (
    <Stack gap={4}>
      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.zones.mapTitle')}</h3>
          <Row gap={3} align="center" wrap>
            {(['SUPPLY_SHORT', 'DEMAND_SHORT', 'BALANCED', 'QUIET', 'UNKNOWN'] as const).map((state) => (
              <Badge key={state} tone={zoneStateTone(state)}>{v('controller.zone_state', state)}</Badge>
            ))}
          </Row>
          {config !== undefined ? (
            <MapCanvas tiles={tileOverride(config.tiles)} center={config.market.center} points={[]} areas={areas} ariaLabel={t('cpt.zones.mapLabel')} pointLabel={() => ''} />
          ) : (
            <Notice tone="neutral">{t('cpt.zones.noTiles')}</Notice>
          )}
        </Stack>
      </Panel>
      <Panel padded={false}>
        <Stack gap={3}>
          <h3>{t('cpt.zones.tableTitle')}</h3>
          <Table
            columns={[
              { key: 'zone', header: t('ctl.log.col.zone'), render: (r: (typeof rows)[number]) => <span>{label(r)}</span> },
              {
                key: 'state', header: t('cpt.zones.col.state'),
                render: (r: (typeof rows)[number]) =>
                  r.data?.latest != null ? <Badge tone={zoneStateTone(r.data.latest.state)}>{v('controller.zone_state', r.data.latest.state)}</Badge> : <Text role="caption" tone="tertiary">{t('cpt.zones.neverEvaluated')}</Text>,
              },
              {
                key: 'did', header: t('cpt.zones.col.did'),
                render: (r: (typeof rows)[number]) => {
                  const d = r.data?.latest;
                  if (d == null) return <span>—</span>;
                  return (
                    <Stack gap={1}>
                      <span>{v('controller.action', d.action)}{d.lever !== 'NONE' ? ` · ${v('active_incentive.lever', d.lever)}` : ''}</span>
                      <Text role="caption" tone="tertiary">{v('controller.reason', d.reasonCode)} · <span className="tnum">{formatMarketInstant(d.decidedAt, tz, lang)}</span></Text>
                    </Stack>
                  );
                },
              },
              {
                key: 'live', header: t('cpt.zones.col.live'),
                render: (r: (typeof rows)[number]) =>
                  (r.data?.live ?? []).length === 0 ? (
                    <Text role="caption" tone="tertiary">{t('cpt.zones.noOffers')}</Text>
                  ) : (
                    <Stack gap={1}>
                      {(r.data?.live ?? []).map((o) => {
                        const c = countdownOf(o.validFrom, o.validTo, nowMs);
                        return (
                          <Row key={o.incentiveId} gap={2} align="center" wrap>
                            <Badge tone={o.source === 'CONTROLLER' ? 'success' : 'neutral'}>{v('active_incentive.source', o.source)}</Badge>
                            <span>{v('active_incentive.lever', o.lever)}</span>
                            <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.zones.offerEnds', { minutes: c.minutes, cap: wireMoney(o.spendCap, lang) })}</span></Text>
                          </Row>
                        );
                      })}
                    </Stack>
                  ),
              },
            ]}
            rows={rows}
            rowKey={(r) => r.zoneId}
            empty={<EmptyState title={zones === undefined ? t('ui.loading') : t('cpt.zones.empty')} />}
          />
        </Stack>
      </Panel>
    </Stack>
  );
}
